/**
 * @file chat-event-meta.ts
 * @description Реестр служебных записей диалога (chat-window-design.md §3,
 * roles.md R4.15): вид → значок, тон, подпись строкой и подробности.
 *
 * Новый вид записи = новая строка реестра, а не ветка в разметке. Подпись —
 * сегментами (глагол, имена, связки): строка ленты рисует их разным весом и
 * цветом, а превью списка и программы чтения экрана берут текст целиком.
 * Слова без пола — причастие к «диалог»: «взят», «передан», «возвращён».
 *
 * Подробности (раскрытие записи) — с ролями участников словами сервера
 * («Анна Кравец, оператор заказов → Пётр Смирнов, логист»; `actorRole`,
 * `fromRole`, `toRole`). Автоматическое «в архив» называет причину
 * (`reason`, R4.17) — реестром `CHAT_TEXT.archiveReason`.
 */

import {
  Archive,
  ArchiveRestore,
  ArrowRightLeft,
  CircleDot,
  Clock,
  Undo2,
  UserCheck,
  UserX,
  type LucideIcon,
} from "lucide-react";

import type { ConversationEventDto, ConversationEventType } from "../../lib/api-chat";
import { fullDateTime } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";

/** Тон записи: синий — передача и взятие, янтарный — внимание, серый — архив. */
export type ChatEventTone = "info" | "warning" | "neutral";

/** Кусок подписи: глагол (500), имя (500, цвет текста), связка (второй цвет). */
export interface EventSegment {
  text: string;
  kind: "verb" | "name" | "link";
}

export interface ChatEventView {
  icon: LucideIcon;
  tone: ChatEventTone;
  /** Сделала система: значок в пунктирном круге, «Автоматически:» в начале. */
  automatic: boolean;
  segments: EventSegment[];
  /** Подробности при раскрытии записи. */
  detail: string;
}

const verb = (text: string): EventSegment => ({ text, kind: "verb" });
const name = (text: string): EventSegment => ({ text, kind: "name" });
const link = (text: string): EventSegment => ({ text, kind: "link" });

type EventPerson = ConversationEventDto["actor"];

/** Имя участника записи; удалённый сотрудник без имени — «сотрудник». */
function who(person: EventPerson): string {
  return person?.name?.trim() || "сотрудник";
}

/**
 * Имя с ролью для подробностей: «Анна Кравец, оператор заказов». Роль —
 * словами сервера на момент ответа; null (система, удалён, не работает) — без неё.
 */
function whoWithRole(person: EventPerson, role: string | null | undefined): string {
  const r = role?.trim();
  return r ? `${who(person)}, ${r.charAt(0).toLowerCase()}${r.slice(1)}` : who(person);
}

const actorFull = (e: ConversationEventDto) => whoWithRole(e.actor, e.actorRole);
const fromFull = (e: ConversationEventDto) => whoWithRole(e.from, e.fromRole);
const toFull = (e: ConversationEventDto) => whoWithRole(e.to, e.toRole);

/** Причина автоматического «в архив» словами; неизвестная — общая фраза. */
function archiveReason(e: ConversationEventDto): string {
  const reasons: Partial<Record<string, string>> = CHAT_TEXT.archiveReason;
  return (e.reason ? reasons[e.reason] : undefined) ?? CHAT_TEXT.archiveReasonUnknown;
}

interface EventMetaEntry {
  icon: LucideIcon;
  tone: ChatEventTone;
  /** Запись автоматическая (actor null) — свой значок и тон, если отличаются. */
  auto?: { icon: LucideIcon; tone: ChatEventTone };
  segments: (e: ConversationEventDto) => EventSegment[];
  detail: (e: ConversationEventDto) => string;
}

/** Реестр видов (таблица §3). */
const EVENT_META: Record<ConversationEventType, EventMetaEntry> = {
  TAKEN: {
    icon: UserCheck,
    tone: "info",
    segments: (e) => [verb("Взят себе"), link(" · "), name(who(e.actor))],
    detail: (e) => `Взял(а) себе: ${actorFull(e)}`,
  },
  TRANSFERRED: {
    icon: ArrowRightLeft,
    tone: "info",
    segments: (e) => [verb("Передан"), link(" · "), name(who(e.actor ?? e.from)), link(" → "), name(who(e.to))],
    detail: (e) => {
      const base = `Передал(а): ${e.actor ? actorFull(e) : fromFull(e)} → ${toFull(e)}`;
      // Оператор передал чужой диалог — кто вёл до передачи.
      return e.from && e.actor && e.from.id !== e.actor.id ? `${base} (вёл: ${fromFull(e)})` : base;
    },
  },
  RETURNED_TO_QUEUE: {
    icon: Undo2,
    tone: "warning",
    segments: (e) => [verb("Возвращён оператору"), link(" · "), name(who(e.actor))],
    detail: (e) => `Вернул(а) оператору: ${actorFull(e)}`,
  },
  ARCHIVED: {
    icon: Archive,
    tone: "neutral",
    auto: { icon: Clock, tone: "neutral" },
    segments: (e) =>
      e.actor ? [verb("В архив"), link(" · "), name(who(e.actor))] : [verb("в архив"), link(` — ${archiveReason(e)}`)],
    detail: (e) => (e.actor ? `Отправил(а) в архив: ${actorFull(e)}` : `Отправлен в архив автоматически: ${archiveReason(e)}`),
  },
  UNARCHIVED: {
    icon: ArchiveRestore,
    tone: "neutral",
    auto: { icon: ArchiveRestore, tone: "info" },
    segments: (e) =>
      e.actor
        ? [verb("Из архива"), link(" · "), name(who(e.actor))]
        : [
            verb("из архива"),
            link(" — клиент написал. "),
            ...(e.to ? [link("Ведёт "), name(who(e.to))] : [link("Без ответственного")]),
          ],
    detail: (e) => {
      if (e.actor) return `Вернул(а) из архива: ${actorFull(e)}`;
      const released = e.from ? `; ${fromFull(e)} больше не может вести переписку` : "";
      const leads = e.to ? `. Ведёт: ${toFull(e)}` : "";
      return `Клиент написал в архивный диалог${released}${leads}`;
    },
  },
  ASSIGNEE_RELEASED: {
    icon: UserX,
    tone: "warning",
    segments: (e) => [verb("ответственный снят"), link(" — "), name(who(e.from)), link(" отключён или лишён права переписки")],
    detail: (e) => `Снят ответственный: ${fromFull(e)}`,
  },
};

/** Код вида словами: `PRICE_CHANGED` → «Price changed» (новый вид с сервера, которого нет в реестре). */
function typeInWords(type: string): string {
  const words = String(type ?? "").replace(/[_-]+/g, " ").trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Служебная запись";
}

/** Запасной вид: сервер прислал запись, которой кабинет ещё не знает, — не падаем. */
function fallbackMeta(type: string): EventMetaEntry {
  const title = typeInWords(type);
  return {
    icon: CircleDot,
    tone: "neutral",
    segments: (e) => (e.actor ? [verb(title), link(" · "), name(who(e.actor))] : [verb(title)]),
    detail: (e) => (e.actor ? `${title}: ${actorFull(e)}` : title),
  };
}

/** Вид записи для ленты, панели сведений и превью. */
export function chatEventView(event: ConversationEventDto): ChatEventView {
  const known = (EVENT_META as Partial<Record<string, EventMetaEntry>>)[event.type];
  const meta = known ?? fallbackMeta(event.type);
  const automatic = event.actor === null && event.type !== "TRANSFERRED";
  const look = automatic && meta.auto ? meta.auto : meta;
  const segments = meta.segments(event);
  return {
    icon: look.icon,
    tone: look.tone,
    automatic,
    segments: automatic ? [link(`${CHAT_TEXT.automatic} `), ...segments] : segments,
    detail: `${meta.detail(event)} · ${fullDateTime(event.at)}`,
  };
}

/** Подпись записи одной строкой: превью списка, «История диалога», aria. */
export function chatEventText(event: ConversationEventDto): string {
  return chatEventView(event).segments.map((s) => s.text).join("");
}

/** Превью в строке списка, когда последнее событие — запись (§2). */
export function chatEventPreview(event: ConversationEventDto, meUserId: string | null): string {
  if (event.type === "TRANSFERRED" && meUserId && event.to?.id === meUserId) return CHAT_TEXT.pillTransferred;
  return chatEventText(event);
}

/** Классы тона: значок в круге 24. */
export const EVENT_TONE_CLASS: Record<ChatEventTone, string> = {
  info: "bg-info/12 text-info",
  warning: "bg-warning/15 text-warning",
  neutral: "bg-surface-low text-on-surface-variant",
};
